import logger from '@eventbrite/client-logger';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { EventDateTimeProps, LocalizedDateTime } from '../../types';
import EventDetailsDateTime from '../EventDateTime/EventDetailsDateTime/EventDetailsDateTime';
import EventHeroDate from '../EventDateTime/EventHeroDate/EventHeroDate';
import { renderOnElement } from '../utils/mountComponent';

const LISTING_HERO_DATE_MOBILE = 'listing-hero-date-mobile';
const LISTING_HERO_DATE_DESKTOP = 'listing-hero-date-desktop';
const LISTING_EVENT_DATE_TIME_MOBILE = 'listing-event-date-time-mobile';
const LISTING_EVENT_DATE_TIME_DESKTOP = 'listing-event-date-time-desktop';

export const getUserTimezone = () => {
    try {
        return new Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e: any) {
        logger.error('User timezone is not defined', e);
        return null;
    }
};

const shouldShowLocalizedDates = (
    isOnlineEvent: boolean,
    userTimezone: string | null,
) => isOnlineEvent && !!userTimezone;

const getLocalizedDate = (date: LocalizedDateTime, isOnlineEvent: boolean) => {
    const userTimezone = getUserTimezone();

    if (shouldShowLocalizedDates(isOnlineEvent, userTimezone)) {
        return moment.tz(date.utc, userTimezone);
    }

    return moment(date.local);
};

export const getFormattedDate = ({
    date,
    format,
    isOnlineEvent,
    localizedFormat,
    locale,
}: {
    date: LocalizedDateTime;
    isOnlineEvent: boolean;
    format: string;
    locale: string;
    localizedFormat?: string;
}) => {
    const userTimezone = getUserTimezone();
    const formatToApply = shouldShowLocalizedDates(isOnlineEvent, userTimezone)
        ? localizedFormat ?? format
        : format;

    return getLocalizedDate(date, isOnlineEvent)
        .locale(locale)
        .format(formatToApply);
};

export const isSameDay = ({
    end,
    isOnlineEvent,
    start,
}: {
    end: LocalizedDateTime;
    start: LocalizedDateTime;
    isOnlineEvent: boolean;
}) =>
    getLocalizedDate(start, isOnlineEvent)
        .startOf('day')
        .diff(getLocalizedDate(end, isOnlineEvent).startOf('day'), 'days') ===
    0;

const eventDetailsDateTimeDesktop = (props: EventDateTimeProps) => {
    renderOnElement(
        {
            Component: EventDetailsDateTime,
            containerId: LISTING_EVENT_DATE_TIME_DESKTOP,
        },
        props,
    );
};

const eventDetailsDateTimeMobile = (props: EventDateTimeProps) => {
    renderOnElement(
        {
            Component: EventDetailsDateTime,
            containerId: LISTING_EVENT_DATE_TIME_MOBILE,
        },
        props,
    );
};

const eventHeroDateDesktop = (props: EventDateTimeProps) => {
    renderOnElement(
        {
            Component: EventHeroDate,
            containerId: LISTING_HERO_DATE_DESKTOP,
        },
        props,
    );
};

const eventHeroDateMobile = (props: EventDateTimeProps) => {
    renderOnElement(
        {
            Component: EventHeroDate,
            containerId: LISTING_HERO_DATE_MOBILE,
        },
        props,
    );
};

export const useLocalizedDates = (props: EventDateTimeProps) =>
    useEffect(() => {
        /*
         * There are still some use cases in which core is
         * returning content that we don't want to replace.
         *
         * Until ebui handles all the use cases regarding dates
         * we have to avoid applying the effect.
         */
        const hideStartAndEndDates = props.hideEndDate && props.hideStartDate;
        const shouldApplyEffect =
            props.isOnlineEvent &&
            !props.isSeriesParent &&
            !hideStartAndEndDates;
        if (shouldApplyEffect) {
            eventDetailsDateTimeDesktop(props);
            eventDetailsDateTimeMobile(props);
            eventHeroDateDesktop(props);
            eventHeroDateMobile(props);
        }
    });
